/**
 * IndexedDB wrapper for offline quote system
 * Uses idb library for promise-based operations
 * Following OBY api-conventions.json: consistent error handling with descriptive context
 */

import { type IDBPDatabase, openDB } from "idb";
import type {
  CachedCustomer,
  CachedMeeting,
  CachedProduct,
  CachedService,
  OfflineDataStore,
  OfflineImage,
  OfflineMeeting,
  OfflineQuote,
  SyncMetadata,
  SyncQueueItem,
} from "./types";
import { DB_NAME, DB_VERSION } from "./types";

type OfflineDB = IDBPDatabase<OfflineDataStore>;

let dbInstance: OfflineDB | null = null;

/**
 * Initialize IndexedDB with schema
 * Creates all necessary object stores with indexes
 */
export async function initializeDB(): Promise<OfflineDB> {
  if (dbInstance) {
    return dbInstance;
  }

  try {
    dbInstance = await openDB<OfflineDataStore>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Pending quotes store
        if (!db.objectStoreNames.contains("pending-quotes")) {
          const quotesStore = db.createObjectStore("pending-quotes", {
            keyPath: "id",
          });
          quotesStore.createIndex("sync_status", "sync_status");
          quotesStore.createIndex("created_at", "created_at");
          quotesStore.createIndex("project_id", "project_id");
        }

        // Pending images store (v3: offline image support)
        if (!db.objectStoreNames.contains("pending-images")) {
          const imagesStore = db.createObjectStore("pending-images", {
            keyPath: "id",
          });
          imagesStore.createIndex("quote_id", "quote_id");
          imagesStore.createIndex("sync_status", "sync_status");
          imagesStore.createIndex("created_at", "created_at");
          imagesStore.createIndex("field_name", "field_name");
        }

        // Cached customers store
        if (!db.objectStoreNames.contains("cached-customers")) {
          const customersStore = db.createObjectStore("cached-customers", {
            keyPath: "id",
          });
          customersStore.createIndex("cached_at", "cached_at");
          customersStore.createIndex("name", "name");
        }

        // Cached products store
        if (!db.objectStoreNames.contains("cached-products")) {
          const productsStore = db.createObjectStore("cached-products", {
            keyPath: "id",
          });
          productsStore.createIndex("cached_at", "cached_at");
          productsStore.createIndex("category", "category");
        }

        // Cached services store
        if (!db.objectStoreNames.contains("cached-services")) {
          const servicesStore = db.createObjectStore("cached-services", {
            keyPath: "id",
          });
          servicesStore.createIndex("cached_at", "cached_at");
        }

        // Offline meetings store (pending sync)
        if (!db.objectStoreNames.contains("offline-meetings")) {
          const meetingsStore = db.createObjectStore("offline-meetings", {
            keyPath: "id",
          });
          meetingsStore.createIndex("sync_status", "sync_status");
          meetingsStore.createIndex("created_at", "created_at");
          meetingsStore.createIndex("customer_id", "customer_id");
          meetingsStore.createIndex("meeting_date", "meeting_date");
          meetingsStore.createIndex("status", "status");
        }

        // Cached meetings store (for offline viewing)
        if (!db.objectStoreNames.contains("cached-meetings")) {
          const cachedMeetingsStore = db.createObjectStore("cached-meetings", {
            keyPath: "id",
          });
          cachedMeetingsStore.createIndex("cached_at", "cached_at");
          cachedMeetingsStore.createIndex("customer_id", "customer_id");
          cachedMeetingsStore.createIndex("meeting_date", "meeting_date");
          cachedMeetingsStore.createIndex("status", "status");
        }

        // Sync queue store
        if (!db.objectStoreNames.contains("sync-queue")) {
          const queueStore = db.createObjectStore("sync-queue", {
            keyPath: "id",
          });
          queueStore.createIndex("type", "type");
          queueStore.createIndex("created_at", "created_at");
          queueStore.createIndex("retry_count", "retry_count");
        }

        // Sync metadata store
        if (!db.objectStoreNames.contains("sync-metadata")) {
          db.createObjectStore("sync-metadata", {
            keyPath: "key",
          });
        }
      },
    });
    return dbInstance;
  } catch (_error) {
    throw new Error("Failed to initialize offline database");
  }
}

/**
 * Get database instance, initializing if needed
 */
async function getDB(): Promise<OfflineDB> {
  if (!dbInstance) {
    return await initializeDB();
  }
  return dbInstance;
}

// ============================================================================
// PENDING QUOTES CRUD
// ============================================================================

export async function savePendingQuote(quote: OfflineQuote): Promise<void> {
  try {
    const db = await getDB();
    await db.put("pending-quotes", quote);
  } catch (_error) {
    throw new Error("Failed to save quote offline");
  }
}

export async function getPendingQuote(id: string): Promise<OfflineQuote | undefined> {
  try {
    const db = await getDB();
    return await db.get("pending-quotes", id);
  } catch (_error) {
    throw new Error("Failed to retrieve offline quote");
  }
}

export async function getAllPendingQuotes(): Promise<OfflineQuote[]> {
  try {
    const db = await getDB();
    return await db.getAll("pending-quotes");
  } catch (_error) {
    throw new Error("Failed to retrieve pending quotes");
  }
}

export async function getPendingQuotesByStatus(
  status: OfflineQuote["sync_status"]
): Promise<OfflineQuote[]> {
  try {
    const db = await getDB();
    return await db.getAllFromIndex("pending-quotes", "sync_status", status);
  } catch (_error) {
    return [];
  }
}

export async function deletePendingQuote(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete("pending-quotes", id);
  } catch (_error) {
    throw new Error("Failed to delete offline quote");
  }
}

export async function updateQuoteStatus(
  id: string,
  status: OfflineQuote["sync_status"],
  error?: string
): Promise<void> {
  try {
    const db = await getDB();
    const quote = await db.get("pending-quotes", id);
    if (!quote) {
      throw new Error(`Quote not found: ${id}`);
    }

    quote.sync_status = status;
    quote.updated_at = new Date().toISOString();
    if (error) {
      quote.last_error = error;
    }

    await db.put("pending-quotes", quote);
  } catch (_error) {
    throw new Error("Failed to update quote status");
  }
}

// ============================================================================
// CACHED CUSTOMERS CRUD
// ============================================================================

export async function cacheCustomers(customers: CachedCustomer[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction("cached-customers", "readwrite");

    await Promise.all([...customers.map((customer) => tx.store.put(customer)), tx.done]);
  } catch (_error) {
    throw new Error("Failed to cache customers");
  }
}

export async function getCachedCustomers(): Promise<CachedCustomer[]> {
  try {
    const db = await getDB();
    return await db.getAll("cached-customers");
  } catch (_error) {
    return [];
  }
}

export async function clearCachedCustomers(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear("cached-customers");
  } catch (_error) {
    throw new Error("Failed to clear cached customers");
  }
}

// ============================================================================
// CACHED PRODUCTS CRUD
// ============================================================================

export async function cacheProducts(products: CachedProduct[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction("cached-products", "readwrite");

    await Promise.all([...products.map((product) => tx.store.put(product)), tx.done]);
  } catch (_error) {
    throw new Error("Failed to cache products");
  }
}

export async function getCachedProducts(): Promise<CachedProduct[]> {
  try {
    const db = await getDB();
    return await db.getAll("cached-products");
  } catch (_error) {
    return [];
  }
}

export async function clearCachedProducts(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear("cached-products");
  } catch (_error) {
    throw new Error("Failed to clear cached products");
  }
}

// ============================================================================
// CACHED SERVICES CRUD
// ============================================================================

export async function cacheServices(services: CachedService[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction("cached-services", "readwrite");

    await Promise.all([...services.map((service) => tx.store.put(service)), tx.done]);
  } catch (_error) {
    throw new Error("Failed to cache services");
  }
}

export async function getCachedServices(): Promise<CachedService[]> {
  try {
    const db = await getDB();
    return await db.getAll("cached-services");
  } catch (_error) {
    return [];
  }
}

export async function clearCachedServices(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear("cached-services");
  } catch (_error) {
    throw new Error("Failed to clear cached services");
  }
}

// ============================================================================
// OFFLINE MEETINGS CRUD
// ============================================================================

export async function saveOfflineMeeting(meeting: OfflineMeeting): Promise<void> {
  try {
    const db = await getDB();
    await db.put("offline-meetings", meeting);
  } catch (_error) {
    throw new Error("Failed to save meeting offline");
  }
}

export async function getOfflineMeeting(id: string): Promise<OfflineMeeting | undefined> {
  try {
    const db = await getDB();
    return await db.get("offline-meetings", id);
  } catch (_error) {
    throw new Error("Failed to retrieve offline meeting");
  }
}

export async function getAllOfflineMeetings(): Promise<OfflineMeeting[]> {
  try {
    const db = await getDB();
    return await db.getAll("offline-meetings");
  } catch (_error) {
    throw new Error("Failed to retrieve offline meetings");
  }
}

export async function getOfflineMeetingsByStatus(
  status: OfflineMeeting["sync_status"]
): Promise<OfflineMeeting[]> {
  try {
    const db = await getDB();
    return await db.getAllFromIndex("offline-meetings", "sync_status", status);
  } catch (_error) {
    return [];
  }
}

export async function getOfflineMeetingsByCustomer(customerId: number): Promise<OfflineMeeting[]> {
  try {
    const db = await getDB();
    return await db.getAllFromIndex("offline-meetings", "customer_id", customerId);
  } catch (_error) {
    return [];
  }
}

export async function deleteOfflineMeeting(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete("offline-meetings", id);
  } catch (_error) {
    throw new Error("Failed to delete offline meeting");
  }
}

export async function updateMeetingStatus(
  id: string,
  status: OfflineMeeting["sync_status"],
  error?: string
): Promise<void> {
  try {
    const db = await getDB();
    const meeting = await db.get("offline-meetings", id);
    if (!meeting) {
      throw new Error(`Meeting not found: ${id}`);
    }

    meeting.sync_status = status;
    meeting.updated_at = new Date().toISOString();
    if (error) {
      meeting.last_error = error;
    }

    await db.put("offline-meetings", meeting);
  } catch (_error) {
    throw new Error("Failed to update meeting status");
  }
}

// ============================================================================
// CACHED MEETINGS CRUD
// ============================================================================

export async function cacheMeetings(meetings: CachedMeeting[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction("cached-meetings", "readwrite");

    await Promise.all([...meetings.map((meeting) => tx.store.put(meeting)), tx.done]);
  } catch (_error) {
    throw new Error("Failed to cache meetings");
  }
}

export async function getCachedMeetings(): Promise<CachedMeeting[]> {
  try {
    const db = await getDB();
    return await db.getAll("cached-meetings");
  } catch (_error) {
    return [];
  }
}

export async function getCachedMeetingsByCustomer(customerId: number): Promise<CachedMeeting[]> {
  try {
    const db = await getDB();
    return await db.getAllFromIndex("cached-meetings", "customer_id", customerId);
  } catch (_error) {
    return [];
  }
}

export async function getCachedMeetingsByDateRange(
  dateFrom: string,
  dateTo: string
): Promise<CachedMeeting[]> {
  try {
    const db = await getDB();
    const allMeetings = await db.getAll("cached-meetings");

    // Filter by date range (meeting_date is YYYY-MM-DD format)
    return allMeetings.filter(
      (meeting) => meeting.meeting_date >= dateFrom && meeting.meeting_date <= dateTo
    );
  } catch (_error) {
    return [];
  }
}

export async function clearCachedMeetings(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear("cached-meetings");
  } catch (_error) {
    throw new Error("Failed to clear cached meetings");
  }
}

// ============================================================================
// SYNC QUEUE CRUD
// ============================================================================

export async function addToSyncQueue(item: SyncQueueItem): Promise<void> {
  try {
    const db = await getDB();
    await db.put("sync-queue", item);
  } catch (_error) {
    throw new Error("Failed to add item to sync queue");
  }
}

export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  try {
    const db = await getDB();
    return await db.getAll("sync-queue");
  } catch (_error) {
    return [];
  }
}

export async function removeFromSyncQueue(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete("sync-queue", id);
  } catch (_error) {
    throw new Error("Failed to remove item from sync queue");
  }
}

export async function clearSyncQueue(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear("sync-queue");
  } catch (_error) {
    throw new Error("Failed to clear sync queue");
  }
}

// ============================================================================
// SYNC METADATA CRUD
// ============================================================================

export async function setSyncMetadata(metadata: SyncMetadata): Promise<void> {
  try {
    const db = await getDB();
    await db.put("sync-metadata", metadata);
  } catch (_error) {
    throw new Error("Failed to set sync metadata");
  }
}

export async function getSyncMetadata(key: SyncMetadata["key"]): Promise<SyncMetadata | undefined> {
  try {
    const db = await getDB();
    return await db.get("sync-metadata", key);
  } catch (_error) {
    return undefined;
  }
}

export async function getAllSyncMetadata(): Promise<SyncMetadata[]> {
  try {
    const db = await getDB();
    return await db.getAll("sync-metadata");
  } catch (_error) {
    return [];
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// ============================================================================
// PENDING IMAGES CRUD (v3: offline image support)
// ============================================================================

export async function savePendingImage(image: OfflineImage): Promise<void> {
  try {
    const db = await getDB();
    await db.put("pending-images", image);
  } catch (_error) {
    throw new Error("Failed to save image offline");
  }
}

export async function getPendingImage(id: string): Promise<OfflineImage | undefined> {
  try {
    const db = await getDB();
    return await db.get("pending-images", id);
  } catch (_error) {
    throw new Error("Failed to retrieve offline image");
  }
}

export async function getPendingImagesByQuote(quoteId: string): Promise<OfflineImage[]> {
  try {
    const db = await getDB();
    return await db.getAllFromIndex("pending-images", "quote_id", quoteId);
  } catch (_error) {
    return [];
  }
}

export async function getPendingImagesByStatus(
  status: OfflineImage["sync_status"]
): Promise<OfflineImage[]> {
  try {
    const db = await getDB();
    return await db.getAllFromIndex("pending-images", "sync_status", status);
  } catch (_error) {
    return [];
  }
}

export async function getAllPendingImages(): Promise<OfflineImage[]> {
  try {
    const db = await getDB();
    return await db.getAll("pending-images");
  } catch (_error) {
    return [];
  }
}

export async function deletePendingImage(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete("pending-images", id);
  } catch (_error) {
    throw new Error("Failed to delete offline image");
  }
}

export async function updateImageStatus(
  id: string,
  status: OfflineImage["sync_status"],
  error?: string,
  uploadedUrl?: string
): Promise<void> {
  try {
    const db = await getDB();
    const image = await db.get("pending-images", id);
    if (!image) {
      throw new Error(`Image not found: ${id}`);
    }

    image.sync_status = status;
    if (error) {
      image.last_error = error;
    }
    if (uploadedUrl) {
      image.uploaded_url = uploadedUrl;
    }

    await db.put("pending-images", image);
  } catch (_error) {
    throw new Error("Failed to update image status");
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Clear all offline data (use with caution)
 */
export async function clearAllOfflineData(): Promise<void> {
  try {
    const db = await getDB();
    await Promise.all([
      db.clear("pending-quotes"),
      db.clear("pending-images"),
      db.clear("offline-meetings"),
      db.clear("cached-customers"),
      db.clear("cached-products"),
      db.clear("cached-services"),
      db.clear("cached-meetings"),
      db.clear("sync-queue"),
      db.clear("sync-metadata"),
    ]);
  } catch (_error) {
    throw new Error("Failed to clear offline data");
  }
}

/**
 * Get storage estimate (quota usage)
 */
export async function getStorageEstimate(): Promise<{
  usage: number;
  quota: number;
  percentage: number;
}> {
  try {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;

      return { usage, quota, percentage };
    }
    return { usage: 0, quota: 0, percentage: 0 };
  } catch (_error) {
    return { usage: 0, quota: 0, percentage: 0 };
  }
}

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return "indexedDB" in window && window.indexedDB !== null;
  } catch {
    return false;
  }
}
